import type { Health } from '@ops/contracts'

import { cx } from '../lib/cx'
import {
  SEVERIDAD_LABEL,
  SEVERIDAD_RELLENO,
  SEVERIDAD_TEXTO,
} from './tokens'

/**
 * Semáforo de una métrica o de una fila.
 *
 * Tres señales redundantes, porque **nunca color solo**:
 *  1. el color de relleno del punto,
 *  2. la **forma** (círculo en target, rombo atención, cuadrado crítico), que es
 *     lo que distingue los estados en un daltonismo rojo-verde,
 *  3. el texto, visible o solo para lectores de pantalla.
 *
 * El punto lleva un anillo interno neutro: los rellenos de marca no llegan a 3:1
 * sobre blanco, así que el contorno es lo que define la figura. No es un color
 * nuevo, es un borde.
 */
export interface HealthDotProps {
  health: Health
  size?: 'sm' | 'md'
  /** false = el texto sigue en el DOM pero solo lo ve un lector de pantalla. */
  showLabel?: boolean
  /** Reemplaza el texto por defecto ("En target", "Atención", …). */
  label?: string
  className?: string
}

const FORMA: Record<Health, string> = {
  ok: 'rounded-full',
  warning: 'rounded-[1px] rotate-45',
  critical: 'rounded-[1px]',
  unknown: 'rounded-full',
}

export function HealthDot({
  health,
  size = 'md',
  showLabel = true,
  label,
  className,
}: HealthDotProps) {
  const texto = label ?? SEVERIDAD_LABEL[health]
  const dimension = size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2'

  return (
    <span
      data-testid="health-dot"
      data-health={health}
      className={cx('inline-flex items-center gap-1.5', className)}
      // Solo cuando el texto está oculto: si es visible, el nombre accesible ya
      // sale del propio contenido y duplicarlo lo lee dos veces.
      aria-label={showLabel ? undefined : texto}
    >
      <span
        aria-hidden="true"
        data-testid="health-dot-punto"
        className={cx(
          'inline-block shrink-0 shadow-[inset_0_0_0_1px_rgba(0,0,0,.32)]',
          dimension,
          FORMA[health],
          SEVERIDAD_RELLENO[health],
        )}
      />
      <span
        className={cx(
          showLabel
            ? cx('text-meta font-medium', SEVERIDAD_TEXTO[health])
            : 'sr-only',
        )}
      >
        {texto}
      </span>
    </span>
  )
}
